import React, { useEffect, useRef, useState } from "react";
import "./ChatSupport.css";
import { NewSideBarMagasins } from "../../NewSidebarMagasin";

interface Message {
  sender: "user" | "admin";
  text: string;
  time: string;
}

export default function ChatSupport() {
  const qa = {
    francais: [
      {
        question: "- Comment envoyer une commande ?",
        answer: `Voici les étapes pour envoyer une commande :
1- Cliquez sur "suivi"
2- Cliquez sur "passez une commande"
3- Remplissez les informations (si vous voulez commander un produit pour votre magasin, laissez le sous client vide et choisissez votre nom de magasin dans "nom de client")
4- Finalement, continuez les étapes suivantes normalement pour compléter votre commande.`,
      },
      {
        question: "Comment puis-je envoyer un devis ?",
        answer: `En quelques étapes simples, envoyer un Devis :
1- Accéder à "gestion de devis"
2- Remplir les informations et sélectionner l'article
3- Cliquez sur "Envoyer"`,
      },
      {
        question: "Comment puis-je ajouter des produits à l'aide d'Excel ?",
        answer: `Suivez ces étapes :
1- Cliquez sur "Articles"
2- Cliquez sur "Ajouter un produit"
3- Le bouton "Importer un fichier Excel" se trouve en haut à droite
4- Le fichier doit contenir les champs indiqués
5- Vos produits sont désormais importés`,
      },
    ],
    arabe: [
      {
        question: "كيفية إرسال الطلب ؟ -",
        answer: `إليك خطوات تقديم الطلب:
1- انقر على "التتبع"
2- انقر على "إرسال طلب"
3- يرجى ملء المعلومات (إذا كنت ترغب في طلب منتج لمتجرك، يُرجى ترك حقل العميل الفرعي فارغًا واختيار اسم متجرك ضمن "اسم العميل").
4- أخيرًا، يمكنك متابعة الخطوات المعتادة لإكمال طلبك.`,
      },
      {
        question: "كيف يمكنني أضافة منتجات بواسطة الاكسل ؟ -",
        answer: `اتبع الخطوات التالية:
1- انقر على "المنتجات"
2- انقر على "إضافة منتج"
3- ستجد زر "استيراد ملف إكسل" في الزاوية العلوية اليمنى
4- يجب أن يحتوي الملف على الحقول المحددة
5- تم الآن استيراد منتجاتك`,
      },
      {
        question: "كيف أرسل عرض أسعار ؟ -",
        answer: `أرسل عرض أسعار بخطوات بسيطة :
1- انتقل إلى "إدارة عروض الأسعار"
2- املأ المعلومات واختر المنتج
3- انقر على "إرسال"`,
      },
    ],
  };

  const initialMessages: Message[] = [
    {
      sender: "admin",
      text: "👋 Bonjour ! Choisir votre langue de parler",
      time: "à l'instant",
    },
  ];

  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [language, setLanguage] = useState<"francais" | "arabe" | null>(null);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const chooseLanguage = (lang: "francais" | "arabe") => {
    setLanguage(lang);
    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        text: lang === "francais" ? "🇫🇷 Français" : "🇲🇦 Arabe",
        time: "à l'instant",
      },
      {
        sender: "admin",
        text: "✅ Parfait. Choisissez une question ci-dessous 👇",
        time: "à l'instant",
      },
    ]);
  };

  const handleQuestionClick = (q: string, a: string) => {
    setInput(q);
    const newUserMsg: Message = { sender: "user", text: q, time: "à l'instant" };
    setMessages((prev) => [...prev, newUserMsg]);
    setInput("");
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const botMsg: Message = { sender: "admin", text: a, time: "à l'instant" };
      setMessages((prev) => [...prev, botMsg]);
    }, 2000);
  };

  const resetConversation = () => {
    setMessages(initialMessages);
    setLanguage(null);
    setInput("");
    setIsTyping(false);
  };

  return (
    <>
      <NewSideBarMagasins />
      <div className="nd-chatm-u-container">

        {/* ── Sidebar ── */}
        <div className="nd-chatm-u-sidebar">
          <p className="nd-chatm-u-sidebar-title">Conversations</p>
          <div className="nd-chatm-u-conversations">
            <div className="nd-chatm-u-conversation nd-chatm-u-active">
              <div className="nd-chatm-u-avatar nd-chatm-u-online">M</div>
              <div className="nd-chatm-u-details">
                <strong>Support</strong>
                <small>24h/24h</small>
              </div>
            </div>
          </div>
        </div>

        {/* ── Chat Panel ── */}
        <div className="nd-chatm-u-chat">

          {/* Header */}
          <div className="nd-chatm-u-header">
            <div className="nd-chatm-u-avatar nd-chatm-u-online">S</div>
            <div>
              <strong>Support</strong>
              <div className="nd-chatm-u-status">En ligne maintenant</div>
            </div>
            <button className="nd-chatm-u-reset-btn" onClick={resetConversation}>
              ↺ Nouveau
            </button>
          </div>

          {/* Body */}
          <div className="nd-chatm-u-body" ref={bodyRef}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`nd-chatm-u-message nd-chatm-u-${msg.sender}`}
              >
                {msg.text}
                <span className="nd-chatm-u-time">{msg.time}</span>
              </div>
            ))}

            {isTyping && (
              <div className="nd-chatm-u-message nd-chatm-u-admin nd-chatm-u-typing">
                <em>est en train d'écrire...</em>
              </div>
            )}

            {!language && !isTyping && (
              <div className="nd-chatm-u-language-choice">
                <button onClick={() => chooseLanguage("francais")}>🇫🇷 Français</button>
                <button onClick={() => chooseLanguage("arabe")}>🇲🇦 Arabe</button>
              </div>
            )}

            {language && !isTyping && (
              <div className="nd-chatm-u-question-list">
                {qa[language].map((item, idx) => (
                  <button
                    key={idx}
                    className="nd-chatm-u-question-btn"
                    onClick={() => handleQuestionClick(item.question, item.answer)}
                  >
                    <span>{item.question}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="nd-chatm-u-footer">
            <input
              placeholder="Écrivez votre message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled
            />
            <button disabled>➤</button>
          </div>

        </div>
      </div>
    </>
  );
}